import { useState, useEffect, useRef } from "react";
import ProductHeader from "../../Components/ProductsPages/ProductHeader";
import ProductsHeroSection from "../../Components/ProductsPages/ProductsHeroSection";
import ProductFooter from "../../Components/ProductsPages/ProductFooter";
import RequestDemo from "../../Components/ProductsPages/RequestDemo";
import BackgroundGradient from "../../Images/BackgroundGradient.png";
import BackgroundGradientSmall from "../../Images/BackgroundGradientSmall.png";
import { KeyFeatures } from "../../Components/KeyFeatures";
import RYGBERG from "../../Images/Boxes/RYGBERG_SENSOR_P1.png";

export default function RydbergSensor() {
  const requestDemoRef = useRef(null);
  const architectureRef = useRef(null);

  const [backgroundImage, setBackgroundImage] = useState(``);

  useEffect(() => {
    const updateBackgroundImage = () => {
      if (window.innerWidth < 640) {
        setBackgroundImage(`url(${BackgroundGradientSmall})`);
      } else {
        setBackgroundImage(`url(${BackgroundGradient})`);
      }
    };

    updateBackgroundImage();

    window.addEventListener("resize", updateBackgroundImage);

    return () => window.removeEventListener("resize", updateBackgroundImage);
  }, []);

  const scrollToRequestDemo = () => {
    requestDemoRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToArchitecture = () => {
    architectureRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const keyFeatures = [
   "Atoms in a compact vapor cell are excited to Rydberg states using precisely tuned lasers (probe + coupling). ", 
"Incoming RF/microwave fields cause measurable shifts in the Rydberg energy levels (viz. Autler-Townes splitting or Stark shift).  ",
"These shifts are read out optically via electromagnetically induced transparency (EIT) or fluorescence. ", 
"The result: direct, absolute measurement of electric field strength and frequency without traditional calibration.",
"This quantum approach delivers SI-traceable measurements with minimal drift and exceptional performance across a wide spectrum — from kHz to THz.",


    "Ultra-wide bandwidth — Covers kHz to THz in a single sensor  ",
"High sensitivity — Detects fields from µV/m to V/m with femtovolt-level resolution ", 
"Self-calibrated & traceable — No need for external calibration standards  ",
"Compact & room-temperature operation — Small vapor cell, no cryogenics  ",
"Interference resilient — Immune to electromagnetic interference that affects classical antennas  ",
"Vector & phase-sensitive detection — Full field characterization possible  ",
      "Passive & low SWaP — Ideal for portable, wearable, or array-based systems  ",


      "Electromagnetic Spectrum Monitoring — 5G/6G, radar, satellite communications  ",
"Defence — Sensitive RF detection, direction finding, electronic warfare  ",
"Quantum Communications — Precise field characterization for QKD and entanglement distribution  ",
"Medical & Bio-electromagnetics — Non-invasive sensing of bioelectric fields  ",
"Metrology & Calibration — Primary standard for RF electric field strength  ",
"Imaging & Mapping — Near-field imaging, antenna characterization, EMC testing  ",


  ];



  return (
    <div className="w-full  flex flex-col gap-0 items-center justify-center bg-primary7">
      <div
        className="w-full width flex flex-col gap-0 items-center justify-center bg-contain bg-repeat"
        style={{ backgroundImage: backgroundImage }}
      >
        <ProductHeader scrollToRequestDemo={scrollToRequestDemo} />
        <div className="w-full h-[25rem] ">
          <ProductsHeroSection
            title1="Quantum-Level Detection"
            title2="Across the Electromagnetic Spectrum"
            content="Advanced quantum RF sensing using Rydberg atoms for ultra-sensitive, calibration-free detection of electric fields across a wide frequency range."
            scrollToArchitecture={scrollToArchitecture}
          />
        </div>

        <div className="w-full bg-primary7 width md:px-10  flex flex-col items-center justify-center pb-10 gap-1">
          <div className="w-[90%] text-center md:text-left text-[1.5rem] md:text-[2rem] 2xl:text-[2.3rem] font-RobotoBold text-white leading-tight px-3 ">
            <span className="wordGradient">
              Rydberg Sensor: Quantum RF & Electric Field Sensing Redefined
            </span>
          </div>

          <div className="w-[90%] h-full   grid grid-cols-1 xl:grid-cols-2  place-items-start  p-3">
            <div className="w-full  flex flex-col items-center justify-center  gap-1">
              <div className="w-full text-white text-left font-RobotoRegular text-xs md:text-sm px-3">
                At QAIG, we introduce the Rydberg Atom Sensor — a revolutionary
                quantum sensor that uses highly excited Rydberg states of
                Rubidium atoms as ultra-sensitive, self-calibrated detectors of
                radio-frequency (RF) electric fields and microwaves. Unlike
                conventional antennas and receivers, Rydberg sensors exploit the
                enormous dipole moments of Rydberg atoms, enabling detection of
                electric fields with extreme sensitivity, broad bandwidth, and
                intrinsic traceability to fundamental constants.
              </div>

              <div className="w-full px-3 flex items-start justify-start">
                <KeyFeatures
                  title="How It Works"
                  features={keyFeatures.slice(0, 5)}
                />
              </div>
              
            </div>

            <div className="w-full  flex flex-col items-start justify-start  gap-1">
              <KeyFeatures
                title="Key Advantages"
                features={keyFeatures.slice(6, 12)}
              />

              <KeyFeatures
                title="Transformative Applications"
                features={keyFeatures.slice(13, 18)}
              />
            </div>
          </div>
          {/* <div className="w-[90%] h-full  grid grid-cols-1 xl:grid-cols-2  place-items-start  ">
            <KeyFeatures
              title="Key Advantages"
              features={keyFeatures.slice(5, 9)}
            />

            <KeyFeatures
              title="Game-Changing Applications"
              features={keyFeatures.slice(22, 29)}
            />
          </div> */}
          <div className="w-[90%] h-full flex flex-col md:flex-row justify-center items-center gap-10 my-10">
            <div className="w-full flex flex-col items-center justify-center">
              <img
                className="w-[21.5rem]  md:w-[600px]  object-contain rounded-lg md:rounded-2xl"
                src={RYGBERG}
              ></img>
              <p className="w-full lg:max-w-[600px] text-center text-xs italic text-white/70 mt-2">
                Figure 1: Rydberg sensor setup showing probe & coupling lasers interacting with atoms in a vapor cell for RF signal detection, even in the presence of interference.
              </p>
            </div>
          </div>
          {/* <TitleContent
            title="Our Advancement"
            content="QAIG has demonstrated the first Made In India Quantum Memory using room temperature Rubidium vapours, and an Entangled Photon Source (EPS) two of the  essential repeater components in lab settings and is progressing toward next phase. We are currently focussing on integrating on quantum memories, efficient photon sources, and swapping modules  targeting deployment in national and commercial quantum networks. "
            scrollToArchitecture={scrollToArchitecture}
          /> */}
          <p className="w-full text-center font-bold  text-xs md:text-sm text-[#1B99D4] mt-2">
            Quantum precision across the entire electromagnetic spectrum.

          </p>
        </div>

        {/* <VideoComponent
          image={QSleeveImage}
          video={"https://thequantum.ai/videos/QSleeveVideo.mp4"}
        /> */}
        <div
          className="w-full bg-primary7 width flex flex-col items-center justify-center gap-2 "
          ref={requestDemoRef}
        >
          <RequestDemo />
        </div>

        <ProductFooter />
      </div>
    </div>
  );
}
